import { Card } from '@/components/ui/card';
import { PricingComponents } from '@/services/valuation.service';
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercentage,
} from '@/utils/helpers';

interface PricingComponentsTableProps {
  showDetails?: boolean;
  pricingComponents: PricingComponents | null;
  currency?: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
}

export const PricingComponentsTable = ({
  pricingComponents,
  showDetails = true,
  currency = 'USD',
}: PricingComponentsTableProps) => {
  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Pricing Components Breakdown
        </h3>
      </div>

      <div className="space-y-6">
        <div key={pricingComponents?.loan_id} className="border rounded-lg p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="font-semibold text-gray-900">
                {pricingComponents?.loan_name}
              </h4>
              <p className="text-sm text-gray-600">
                Balance: {formatCurrency(pricingComponents?.current_balance, 2, currency)}
              </p>
            </div>
            {pricingComponents?.npv_methodology !== 'RECURSIVE_V2_EXCEL_ALIGNED' && (
              <div className="text-right">
                <p className="text-sm text-gray-600">NPV Method</p>
                <p className="text-sm font-medium">
                  {pricingComponents?.npv_methodology}
                </p>
              </div>
            )}
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Yield Components */}
            <div className="bg-blue-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Coupon Rate</p>
              <p className="font-semibold">
                {pricingComponents?.coupon || 'N/A'}
              </p>
            </div>

            <div className="bg-blue-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">
                Benchmark ({pricingComponents?.benchmark_type})
              </p>
              <p className="font-semibold">
                {formatPercentage(pricingComponents?.benchmark_yield, 3)}
              </p>
            </div>

            <div className="bg-blue-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Market Yield CBE</p>
              <p className="font-semibold">
                {formatPercentage(pricingComponents?.total_yield_pct, 3)}
              </p>
            </div>

            {/* Spread Components */}
            <div className="bg-green-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Base Spread</p>
              <p className="font-semibold">
                {formatNumber(pricingComponents?.rpx_base_spread_bps, 1) +
                  'bps'}
              </p>
            </div>

            <div className="bg-green-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Credit Spread</p>
              <p className="font-semibold">
                {formatNumber(pricingComponents?.rpx_total_spread_bps, 1) +
                  'bps'}
              </p>
            </div>

            <div className="bg-green-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Accrued Interest</p>
              <p className="font-semibold">
                {formatCurrency(pricingComponents?.accrued_interest, 2, currency)}
              </p>
            </div>

            {/* Valuation Results */}
            <div className="bg-purple-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Fair Value (Clean)</p>
              <p className="font-semibold">
                {formatCurrency(pricingComponents?.fair_value_clean, 2, currency)}
              </p>
            </div>

            <div className="bg-purple-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Fair Value (Dirty)</p>
              <p className="font-semibold">
                {formatCurrency(pricingComponents?.fair_value_dirty, 2, currency)}
              </p>
            </div>

            <div className="bg-purple-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">NPV</p>
              <p className="font-semibold">
                {formatCurrency(pricingComponents?.npv_value, 2, currency)}
              </p>
            </div>

            {/* Price Components */}
            <div className="bg-orange-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Clean Price</p>
              <p className="font-semibold">
                {formatPercentage(pricingComponents?.price_clean_pct, 5)}
              </p>
            </div>

            <div className="bg-orange-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Dirty Price</p>
              <p className="font-semibold">
                {formatPercentage(pricingComponents?.price_dirty_pct, 5)}
              </p>
            </div>

            <div className="bg-orange-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Accrued Price %</p>
              <p className="font-semibold">
                {formatPercentage(pricingComponents?.price_accrued_pct, 5)}
              </p>
            </div>

            {/* Proceeds */}
            {showDetails && (
              <>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Gross Proceeds</p>
                  <p className="font-semibold">
                    {formatCurrency(pricingComponents?.gross_proceeds, 2, currency)}
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Net Proceeds</p>
                  <p className="font-semibold">
                    {formatCurrency(pricingComponents?.net_proceeds, 2, currency)}
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Last Updated</p>
                  <p className="font-semibold text-xs">
                    {formatDate(pricingComponents?.last_updated)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {pricingComponents === null && (
        <div className="text-center py-8 text-gray-500">
          No pricing data available for this loan.
        </div>
      )}
    </Card>
  );
};
